// Port of ContentDisplay() in android/.../ui/MainActivity.kt.
// Owns the carousel loop:
//   - filters out interactive items when offline (their iframes would just sit
//     blank waiting for a network that isn't coming)
//   - picks the right renderer per item type
//   - advances the index on each child's onFinished
//   - reports onItemAdvanced upward so usePlayerState can swap in any
//     content_update that arrived mid-rotation (silent update, no cut)
//   - shows the empty "No content assigned" state when there are no items
//   - 600ms opacity crossfade between items (two-layer technique so the
//     outgoing item is still visible while the incoming one fades in)
//
// When the backend sends a zoned `layout`, ContentDisplay renders the split
// grid (LayoutDisplay) instead of a single full-screen rotation — each zone
// runs its own independent Carousel over the zone's item stream.

import { useEffect, useRef, useState } from 'react'
import type { ContentItem, ContentLayout, ContentResponse } from '../lib/deviceApi'
import { isImageUrl, isVideoUrl, isPdfUrl, pickVariantUrl } from '../lib/contentUrl'
import HtmlFrameDisplay from './HtmlFrameDisplay'
import ImageDisplay from './ImageDisplay'
import VideoDisplay from './VideoDisplay'
import PdfDisplay from './PdfDisplay'

const CROSSFADE_MS = 600
const DEFAULT_DURATION_S = 10

// ── Content-item transitions ───────────────────────────────────────────────
// The manifest carries the operator-chosen transition + speed; we map the
// catalog key to the incoming layer's animation class (see index.css) and the
// speed to a duration. Unknown / 'default' → the built-in fade crossfade.
const TR_SPEED_MS: Record<string, number> = { slow: 1000, normal: 600, fast: 300 }
const TR_FAMILY: Record<string, string> = {
  cut: 'tr-cut', fade: 'tr-fade', dissolve: 'tr-dissolve', 'fade-black': 'tr-fade',
  'slide-left': 'tr-slide-left', 'slide-right': 'tr-slide-right',
  'slide-up': 'tr-slide-up', 'slide-down': 'tr-slide-down',
  'push-left': 'tr-slide-left', 'push-right': 'tr-slide-right',
  'zoom-in': 'tr-zoom-in', 'zoom-out': 'tr-zoom-out', 'zoom-blur': 'tr-zoom-blur',
  'flip-h': 'tr-flip-h', 'flip-v': 'tr-flip-v', rotate: 'tr-rotate', spin: 'tr-spin',
  'wipe-left': 'tr-wipe-left', 'wipe-up': 'tr-wipe-up', cube: 'tr-cube',
}
function transitionOf(response: ContentResponse): { inClass: string; outClass: string; durMs: number } {
  const key = response.transition || 'default'
  // The default (and legacy 'cut') is a hard cut — no animation.
  const isCut = key === 'cut' || key === 'default'
  const family = TR_FAMILY[key] || 'tr-fade'   // unknown → fade crossfade
  const durMs = isCut ? 1 : (TR_SPEED_MS[response.transition_speed || 'normal'] ?? CROSSFADE_MS)
  return {
    inClass:  isCut ? 'tr-cut'     : `tr-stage-in ${family}`,
    outClass: isCut ? 'tr-out-cut' : 'tr-out',
    durMs,
  }
}

interface Props {
  response:       ContentResponse
  // Screen's content aspect — drives which pre-cropped variant the carousel
  // picks per item (vertical → 9:16, horizontal → 16:9). The rotation lives
  // on the rotated stage in App.tsx, so it doesn't matter to this component.
  orientationType: 'horizontal' | 'vertical'
  isOnline:       boolean | null
  audioUnlocked?: boolean
  // When a stop-screen overlay is covering this content, the App pauses it so
  // only the overlay's video decodes (no double-decode lag) — the content
  // stays mounted (warm) and resumes instantly on reveal. Currently only
  // VideoDisplay acts on this (the dominant decode cost); image/pdf tiles just
  // keep their cheap timers.
  paused?:        boolean
  onError:        (msg: string) => void
  onRetry:        (itemName: string) => void
  onItemAdvanced: () => void
}

export default function ContentDisplay({
  response, orientationType, isOnline, audioUnlocked = false, paused = false, onError, onRetry, onItemAdvanced,
}: Props) {
  // Zoned layout takes over the whole stage when present — render the grid
  // rather than the flat rotation. Otherwise fall back to the single-zone
  // full-screen carousel over response.items.
  if (response.layout && response.layout.zones.length > 0) {
    return (
      <LayoutDisplay
        layout={response.layout}
        response={response}
        orientationType={orientationType}
        isOnline={isOnline}
        audioUnlocked={audioUnlocked}
        paused={paused}
        onError={onError}
        onRetry={onRetry}
        onItemAdvanced={onItemAdvanced}
      />
    )
  }

  return (
    <Carousel
      items={response.items}
      response={response}
      orientationType={orientationType}
      isOnline={isOnline}
      audioUnlocked={audioUnlocked}
      paused={paused}
      onError={onError}
      onRetry={onRetry}
      onItemAdvanced={onItemAdvanced}
    />
  )
}

// ── LayoutDisplay — zoned split grid ───────────────────────────────────────
// Builds a CSS grid from the layout's resolved track weights (fr units) and
// seats each zone via grid-column / grid-row start+span. Each zone hosts its
// own Carousel so zones with different content lengths rotate independently —
// matching the designer's ZonePreviewFrame in LayoutDetailsPage.
function LayoutDisplay({
  layout, response, orientationType, isOnline, audioUnlocked = false, paused = false,
  onError, onRetry, onItemAdvanced,
}: {
  layout:          ContentLayout
  response:        ContentResponse
  orientationType: 'horizontal' | 'vertical'
  isOnline:        boolean | null
  audioUnlocked?:  boolean
  paused?:         boolean
  onError:         (msg: string) => void
  onRetry:         (itemName: string) => void
  onItemAdvanced:  () => void
}) {
  const totalItems = layout.zones.reduce((n, z) => n + z.items.length, 0)
  if (totalItems === 0) return <EmptyState />

  const cols = layout.column_weights.length
    ? layout.column_weights
    : Array.from({ length: Math.max(1, layout.cols) }, () => 1)
  const rows = layout.row_weights.length
    ? layout.row_weights
    : Array.from({ length: Math.max(1, layout.rows) }, () => 1)

  return (
    <div
      className="absolute inset-0 bg-bg"
      style={{
        display: 'grid',
        gridTemplateColumns: cols.map(w => `${Math.max(0.05, w)}fr`).join(' '),
        gridTemplateRows:    rows.map(w => `${Math.max(0.05, w)}fr`).join(' '),
        // Thin, fixed seam between zones — matches the designer's 2px grid gap
        // (LayoutDetailsPage). A px value (not fr/%) keeps it small and stops
        // it from expanding as the stage scales; the grid's bg-bg shows through
        // as a brand-black hairline so the zones read as distinct sections.
        gap: '2px',
      }}
    >
      {layout.zones.map(zone => (
        <div
          key={zone.id}
          className="relative overflow-hidden bg-bg"
          style={{
            gridColumn: `${zone.col} / span ${Math.max(1, zone.col_span)}`,
            gridRow:    `${zone.row} / span ${Math.max(1, zone.row_span)}`,
          }}
        >
          <Carousel
            items={zone.items}
            response={response}
            orientationType={orientationType}
            isOnline={isOnline}
            audioUnlocked={audioUnlocked}
            paused={paused}
            // Fill the whole zone (cover) so adjacent regions butt up against
            // each other with no letterbox bands between them — a contained
            // image leaves black margins inside its zone that read as gaps in
            // the grid. 'fit' crops to fill, edge-to-edge.
            scaling="fit"
            onError={onError}
            onRetry={onRetry}
            onItemAdvanced={onItemAdvanced}
            // Empty zones stay blank rather than each shouting the full
            // "No content assigned" message.
            emptyFallback={null}
          />
        </div>
      ))}
    </div>
  )
}

// ── Carousel — one rotating content stream ─────────────────────────────────
// The single-zone rotation engine: crossfades through `items`, advancing on
// each tile's onFinished. Used full-screen for flat content and once per zone
// for layouts.
interface CarouselProps {
  items:           ContentItem[]
  response:        ContentResponse
  orientationType: 'horizontal' | 'vertical'
  isOnline:        boolean | null
  audioUnlocked?:  boolean
  // Override the screen scaling for this stream (zones force 'default'/contain).
  scaling?:        'default' | 'fit'
  paused?:         boolean
  onError:         (msg: string) => void
  onRetry:         (itemName: string) => void
  onItemAdvanced:  () => void
  // What to show when the stream has no items. Defaults to the full empty
  // state; zones pass null so an empty region is simply blank.
  emptyFallback?:  React.ReactNode
}

function Carousel({
  items: rawItems, response, orientationType, isOnline, audioUnlocked = false,
  scaling, paused = false, onError, onRetry, onItemAdvanced, emptyFallback,
}: CarouselProps) {
  // Interactive items need an iframe + live network; drop them when offline
  // so the carousel never freezes on a blank tile.
  const items = rawItems.filter(
    item => isOnline !== false || !item.is_interactive,
  )

  // Reset index when the items array content changes (a content swap landed),
  // otherwise the index would persist into a possibly-different rotation.
  const [currentIndex, setCurrentIndex] = useState(0)
  const [previousItem, setPreviousItem] = useState<ContentItem | null>(null)
  const lastIdsRef = useRef<string>('')
  const advancedRef = useRef(false)

  useEffect(() => {
    const ids = items.map(i => i.id).join(',')
    if (ids !== lastIdsRef.current) {
      lastIdsRef.current = ids
      setCurrentIndex(0)
      setPreviousItem(null)
    }
  }, [items])

  advancedRef.current = false  // reset each render so the live tile can advance

  if (items.length === 0) {
    return emptyFallback === undefined ? <EmptyState /> : <>{emptyFallback}</>
  }

  const current = items[currentIndex % items.length]
  const effScaling: 'default' | 'fit' = scaling ?? response.scaling ?? 'default'
  const { inClass, outClass, durMs } = transitionOf(response)
  const trStyle = { ['--tr-dur' as string]: `${durMs}ms` } as React.CSSProperties

  const handleFinished = (item: ContentItem) => {
    // Guard against double-advance: each tile's safety valve + main timer
    // both call onFinished. Only the first wins.
    if (advancedRef.current) return
    if (item.id !== current.id) return
    advancedRef.current = true
    // Single-item rotation: there's nowhere to advance to. Bumping
    // currentIndex would change the React `key` on the live tile, forcing an
    // unmount/remount of the <img> — visible as a brief reload/flicker every
    // N seconds for what should be a static image. So for single-item content
    // we just call onItemAdvanced (so pendingContent can apply any silent
    // refresh that arrived) but leave currentIndex alone.
    if (items.length > 1) {
      setPreviousItem(current)
      setCurrentIndex(i => i + 1)
      window.setTimeout(() => setPreviousItem(null), durMs)
    }
    onItemAdvanced()
  }

  return (
    <div className="absolute inset-0">
      {previousItem && (
        <div key={`prev-${previousItem.id}`} className={`absolute inset-0 ${outClass}`} style={trStyle}>
          {renderItem({
            item:       previousItem,
            isLive:     false,
            response,
            orientationType,
            scaling:    effScaling,
            onError, onRetry, onFinished: () => {},
          })}
        </div>
      )}
      <div key={`cur-${current.id}-${currentIndex}`} className={`absolute inset-0 ${inClass}`} style={trStyle}>
        {renderItem({
          item:       current,
          isLive:     true,
          response,
          orientationType,
          scaling:    effScaling,
          audioUnlocked,
          paused,
          onError, onRetry,
          onFinished: () => handleFinished(current),
        })}
      </div>
    </div>
  )
}

// ── Per-item dispatch ──────────────────────────────────────────────────────

interface RenderProps {
  item:           ContentItem
  isLive:         boolean   // false → this is the outgoing crossfade copy; suppress timers
  response:       ContentResponse  // for screen-level display settings (audio)
  orientationType: 'horizontal' | 'vertical'
  scaling:        'default' | 'fit'
  audioUnlocked?: boolean
  paused?:        boolean
  onError:        (msg: string) => void
  onRetry:        (itemName: string) => void
  onFinished:     () => void
}

function renderItem({ item, isLive, response, orientationType, scaling, audioUnlocked = false, paused = false, onError, onRetry, onFinished }: RenderProps) {
  const durationMs = Math.max(1, (item.duration ?? DEFAULT_DURATION_S)) * 1000
  const namedError = (msg: string) => onError(`[${item.name}] ${msg}`)
  const namedRetry = ()            => onRetry(item.name)

  // Outgoing crossfade copy: don't let it advance the carousel (its onFinished
  // is a no-op already). Render it without timers wiring back to state.
  const safeFinished = isLive ? onFinished : () => {}

  // Resolve the effective URL: images may carry a pre-cropped variant
  // matching the screen orientation. Videos / menus / unknown types just
  // use the canonical url. The image-type detection still runs against the
  // canonical so a 16:9-only legacy upload is still recognised as an image
  // even when its variants dict is empty.
  const effectiveUrl = pickVariantUrl(item, orientationType)

  if (item.type === 'menu' && item.render_url) {
    return (
      <HtmlFrameDisplay
        renderUrl={item.render_url}
        linkUrl={item.link_url}
        itemName={item.name}
        durationMs={durationMs}
        onFinished={safeFinished}
        onError={namedError}
      />
    )
  }
  if ((item.type === 'visual' || item.type === 'media') && isImageUrl(item.url)) {
    return (
      <ImageDisplay
        url={effectiveUrl}
        durationMs={durationMs}
        scaling={scaling}
        onFinished={safeFinished}
        onError={namedError}
        onRetry={namedRetry}
      />
    )
  }
  if ((item.type === 'visual' || item.type === 'media') && isVideoUrl(item.url)) {
    return (
      <VideoDisplay
        url={item.url!}
        durationMs={durationMs}
        scaling={scaling}
        audioUnmuted={response.audio_unmuted}
        videoMuted={response.video_muted}
        volume={response.volume ?? 100}
        itemMuted={item.muted}
        audioUnlocked={audioUnlocked}
        paused={paused}
        onFinished={safeFinished}
        onError={namedError}
        onRetry={namedRetry}
      />
    )
  }
  // PDF media (raw "Document" upload): rasterize every page with pdf.js and
  // stack them into one continuous, scrollable surface — no browser viewer
  // chrome. (PDFs imported "from a folder" / convert-to-images become image
  // items upstream and take the ImageDisplay path above.)
  if ((item.type === 'visual' || item.type === 'media') && isPdfUrl(item.url)) {
    return (
      <PdfDisplay
        url={item.url!}
        durationMs={durationMs}
        scaling={scaling}
        onFinished={safeFinished}
        onError={namedError}
      />
    )
  }
  return <FallbackTile item={item} durationMs={durationMs} onFinished={safeFinished} />
}

// ── Fallback for unknown / typeless items ──────────────────────────────────

function FallbackTile({
  item, durationMs, onFinished,
}: { item: ContentItem; durationMs: number; onFinished: () => void }) {
  useEffect(() => {
    const t = window.setTimeout(onFinished, durationMs)
    return () => window.clearTimeout(t)
  }, [item.id, durationMs, onFinished])
  return (
    <div className="absolute inset-0 bg-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-2 px-10 text-center">
        <span className="text-white text-2xl font-bold">{item.name}</span>
        <span className="text-brand text-sm">[{item.type}]</span>
      </div>
    </div>
  )
}

// ── Empty state ────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    // Top-anchored Frame mark (mirrors BrandHeader on the state screens) so the
    // "no content" screen still reads as branded rather than a bare message.
    // brightness-0 invert forces it to pure white regardless of the source
    // asset's colors — same technique BrandHeader uses.
    <div className="absolute inset-0 flex flex-col">
      <div className="w-full flex justify-center pt-[clamp(1rem,5vh,2.5rem)]">
        <img src="/frame-logo.avif" alt="Frame" className="h-[clamp(2.25rem,9vw,3.5rem)] w-auto brightness-0 invert" />
      </div>
      <div className="flex-1 flex items-center justify-center px-10 text-center">
        <div className="flex flex-col items-center gap-4 max-w-md">
          <h2 className="text-white text-3xl font-semibold mt-1">No content assigned</h2>
          <p className="text-muted text-base leading-relaxed">
            Go to the WeTook dashboard and assign
            <br />menus or media to this screen.
          </p>
        </div>
      </div>
    </div>
  )
}
